const POLL_INTERVAL_MS = 250;
const MAX_POLL_MS = 8000;
const DEFAULT_BODY_FILTER_BOUNDS = {
  heightMin: 140,
  heightMax: 190,
  weightMin: 40,
  weightMax: 95,
};
const PRODUCT_PAGE_REVIEW_PREVIEW_LIMIT = 2;
const PRODUCT_PAGE_PHOTO_PREVIEW_LIMIT = 6;
const INLINE_EXPANDED_REVIEW_BREAKPOINT = '(min-width: 750px)';
const REVIEW_ROW_TOGGLE_IGNORE_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'details',
  '[role="button"]',
  '[data-judge-me-open-media-overlay]',
  '[data-judge-me-media-overlay-view-all]',
  '[data-judge-me-inline-helpful]',
  '.jdgm-helpful',
].join(',');

const htmlEntityTextarea = document.createElement('textarea');

const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
const dateFormatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const CUSTOM_ANSWER_ALIASES = {
  height: ['height', 'heightcm', 'bodyheight', 'customerheight', 'what is your height', '키', '신장'],
  weight: ['weight', 'bodyweight', 'customerweight', 'what is your weight', '몸무게', '체중'],
  fit: ['fit', 'fitsize', 'sizefit', 'sizefeel', 'sizing', 'how was the fit', '핏', '사이즈', '사이즈감', '사이즈느낌', '착용감'],
  purchasedSize: [
    'size',
    'productsize',
    'purchasedsize',
    'orderedsize',
    'wearingsize',
    'what size did you buy',
    'what size did you order',
    'what size did you purchase',
    'what size did you get',
    'what size are you wearing',
    '구매사이즈',
    '착용사이즈',
    '제품사이즈',
    '사이즈옵션',
  ],
};

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toInteger = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const roundDown = (value, step = 5) => Math.floor(value / step) * step;
const roundUp = (value, step = 5) => Math.ceil(value / step) * step;

const decodeHtmlEntities = (value) => {
  const source = String(value ?? '');
  if (!source || !source.includes('&')) return source;

  htmlEntityTextarea.innerHTML = source;
  return htmlEntityTextarea.value;
};

const normalizeText = (value) => decodeHtmlEntities(String(value ?? '')).replace(/\s+/g, ' ').trim();

const stripHtml = (value) => normalizeText(String(value ?? '').replace(/<[^>]*>/g, ' '));

const normalizeLookupKey = (value) =>
  normalizeText(value)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/g, '');

const formatCount = (value) => numberFormatter.format(Math.max(0, toInteger(value, 0)));
const formatMeasure = (value, unit) => `${formatCount(value)} ${unit}`;

const formatDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return dateFormatter.format(date);
};

const buildExcerpt = (value, maxLength = 220) => {
  const text = stripHtml(value);
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
};

const isInlineExpandedReviewLayout = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(INLINE_EXPANDED_REVIEW_BREAKPOINT).matches;

const buildHistogramFromRatings = (ratings) =>
  [5, 4, 3, 2, 1].map((rating) => {
    const frequency = ratings.filter((value) => value === rating).length;
    return {
      rating,
      frequency,
      percentage: ratings.length > 0 ? (frequency / ratings.length) * 100 : 0,
    };
  });

const valueToText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => valueToText(item)).filter(Boolean).join(', ');
  }

  if (value && typeof value === 'object') {
    const objectCandidates = [
      value.value,
      value.label,
      value.text,
      value.name,
      value.title,
      value.content,
      value.display_value,
      value.displayValue,
    ]
      .map((candidate) => normalizeText(candidate))
      .filter(Boolean);

    return objectCandidates[0] || '';
  }

  return normalizeText(value);
};

const buildAnswerCandidates = (labelOrQuestionName) => {
  const inputs = Array.isArray(labelOrQuestionName) ? labelOrQuestionName : [labelOrQuestionName];
  const candidates = new Set();

  inputs.forEach((input) => {
    const normalizedInput = normalizeLookupKey(input);
    if (!normalizedInput) return;

    candidates.add(normalizedInput);

    Object.entries(CUSTOM_ANSWER_ALIASES).forEach(([group, aliases]) => {
      const normalizedGroup = normalizeLookupKey(group);
      const normalizedAliases = aliases.map((alias) => normalizeLookupKey(alias));
      if (normalizedInput === normalizedGroup || normalizedAliases.includes(normalizedInput)) {
        candidates.add(normalizedGroup);
        normalizedAliases.forEach((alias) => candidates.add(alias));
      }
    });
  });

  return candidates;
};

const createCustomAnswer = (label, value, raw) => {
  const normalizedLabel = normalizeText(label);
  const normalizedValue = normalizeText(value);
  if (!normalizedLabel || !normalizedValue) return null;

  return {
    label: normalizedLabel,
    value: normalizedValue,
    lookupKeys: [normalizeLookupKey(normalizedLabel)].filter(Boolean),
    raw,
  };
};

const getEntryLabels = (entry) =>
  [
    entry.description_label,
    entry.descriptionLabel,
    entry.label,
    entry.question,
    entry.question_title,
    entry.question_name,
    entry.questionName,
    entry.field_label,
    entry.fieldLabel,
    entry.name,
    entry.key,
    entry.slug,
    entry.title,
  ]
    .map((value) => normalizeText(value))
    .filter(Boolean);

const getEntryValue = (entry) => {
  const directCandidates = [
    entry.answer,
    entry.value,
    entry.display_value,
    entry.displayValue,
    entry.text,
    entry.content,
    entry.selected_option,
    entry.selectedOption,
    entry.option,
    entry.selection,
  ]
    .map((value) => valueToText(value))
    .filter(Boolean);

  if (directCandidates.length > 0) return directCandidates[0];

  const arrayCandidates = [entry.selected_options, entry.selectedOptions, entry.options, entry.choices]
    .filter(Array.isArray)
    .map((value) => valueToText(value))
    .filter(Boolean);

  return arrayCandidates[0] || '';
};

const looksLikeCustomAnswerEntry = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return [
    'answer',
    'value',
    'display_value',
    'displayValue',
    'question',
    'question_name',
    'questionName',
    'label',
    'description_label',
    'descriptionLabel',
    'field_label',
    'fieldLabel',
    'name',
    'selected_option',
    'selected_options',
  ].some((key) => key in value);
};

const parseCustomAnswerEntry = (entry) => {
  if (entry == null) return null;

  if (Array.isArray(entry)) {
    return entry.map((item) => parseCustomAnswerEntry(item)).flat().filter(Boolean);
  }

  if (typeof entry !== 'object') {
    return [];
  }

  if (looksLikeCustomAnswerEntry(entry)) {
    const labels = getEntryLabels(entry);
    const value = getEntryValue(entry);
    if (!labels.length || !value) return [];
    return [createCustomAnswer(labels[0], value, entry)].filter(Boolean);
  }

  return Object.entries(entry)
    .map(([key, value]) => createCustomAnswer(key, valueToText(value), { key, value }))
    .filter(Boolean);
};

const normalizeCustomFormAnswers = (cfAnswers) => {
  const entries = Array.isArray(cfAnswers)
    ? cfAnswers.flatMap((entry) => parseCustomAnswerEntry(entry))
    : parseCustomAnswerEntry(cfAnswers);

  const deduped = [];
  const seen = new Set();

  entries.forEach((entry) => {
    const signature = `${entry.label}::${entry.value}`;
    if (seen.has(signature)) return;
    seen.add(signature);
    deduped.push(entry);
  });

  return deduped;
};

function getCustomAnswer(review, labelOrQuestionName) {
  const answers = Array.isArray(review?.customFormAnswers) ? review.customFormAnswers : [];
  if (!answers.length) return '';

  const candidates = buildAnswerCandidates(labelOrQuestionName);
  if (!candidates.size) return '';

  const match = answers.find((answer) =>
    answer.lookupKeys.some((lookupKey) => candidates.has(lookupKey)) ||
    candidates.has(normalizeLookupKey(answer.label))
  );

  return match?.value || '';
}

const hasRenderableJudgeMeData = (data) => {
  if (!data || typeof data !== 'object') return false;

  const histogramRows = Array.isArray(data.histogram) ? data.histogram : [];
  const hasReviewCount = Math.max(0, toInteger(data.number_of_reviews, 0)) > 0;
  const hasAverageRating = toFiniteNumber(data.average_rating, 0) > 0;
  const hasReviewsArray = Array.isArray(data.reviews) && data.reviews.length > 0;
  const hasHistogram = histogramRows.some((item) => {
    const frequency = Math.max(0, toInteger(item?.frequency, 0));
    const percentage = clamp(toFiniteNumber(item?.percentage, 0), 0, 100);
    return frequency > 0 || percentage > 0;
  });

  return hasReviewCount || hasAverageRating || hasReviewsArray || hasHistogram;
};

function getJudgeMeReviewWidgetData(productId) {
  const normalizedProductId = String(productId || '').trim();
  if (!normalizedProductId) return null;

  const reviewWidgetData = window.jdgm?.data?.reviewWidget;
  if (!reviewWidgetData || typeof reviewWidgetData !== 'object') return null;

  const widgetData = reviewWidgetData[normalizedProductId] ?? reviewWidgetData[toInteger(normalizedProductId, -1)] ?? null;
  return hasRenderableJudgeMeData(widgetData) ? widgetData : null;
}

function getJudgeMeReviewSummary(productId) {
  const data = getJudgeMeReviewWidgetData(productId);
  if (!data) return null;

  const reviewRatings = Array.isArray(data.reviews)
    ? data.reviews.map((review) => clamp(toInteger(review?.rating, 0), 0, 5)).filter((rating) => rating > 0)
    : [];
  const derivedReviewCount = reviewRatings.length;
  const derivedAverageRating =
    derivedReviewCount > 0 ? reviewRatings.reduce((sum, rating) => sum + rating, 0) / derivedReviewCount : 0;
  const histogram = Array.isArray(data.histogram)
    ? data.histogram.map((item) => ({
        rating: clamp(toInteger(item?.rating, 0), 0, 5),
        frequency: Math.max(0, toInteger(item?.frequency, 0)),
        percentage: clamp(toFiniteNumber(item?.percentage, 0), 0, 100),
      }))
    : [];
  const normalizedReviewCount = Math.max(Math.max(0, toInteger(data.number_of_reviews, 0)), derivedReviewCount);
  const normalizedAverageRating = Math.max(toFiniteNumber(data.average_rating, 0), derivedAverageRating);
  const normalizedHistogram =
    histogram.some((item) => item.frequency > 0 || item.percentage > 0) || derivedReviewCount === 0
      ? histogram
      : buildHistogramFromRatings(reviewRatings);

  return {
    averageRating: normalizedAverageRating,
    numberOfReviews: normalizedReviewCount,
    numberOfQuestions: Math.max(0, toInteger(data.number_of_questions, 0)),
    reviewSummaryText: normalizeText(data.review_summary_text),
    histogram: normalizedHistogram,
  };
}

const getReviewPictureUrl = (picture) => {
  if (typeof picture === 'string') return normalizeText(picture);
  if (!picture || typeof picture !== 'object') return '';

  return [
    picture.original_url,
    picture.originalUrl,
    picture.image_url,
    picture.imageUrl,
    picture.url,
    picture.src,
    picture.href,
    picture.thumbnail_url,
    picture.thumbnailUrl,
  ]
    .map((value) => normalizeText(value))
    .find(Boolean) || '';
};

const getReviewPictures = (review) => {
  const pictureSources = [
    review?.pictures_urls,
    review?.pictures,
    review?.photos,
    review?.images,
    review?.media,
    review?.review_pictures,
    review?.reviewPictures,
    review?.attachments,
  ].filter(Array.isArray);

  const urls = pictureSources.flatMap((source) => source.map((picture) => getReviewPictureUrl(picture))).filter(Boolean);
  return Array.from(new Set(urls));
};

const getReviewHelpfulCount = (review) =>
  Math.max(
    0,
    toInteger(
      review?.helpful_count ??
        review?.helpfulCount ??
        review?.helpful_votes ??
        review?.helpfulVotes ??
        review?.votes_up ??
        review?.votesUp ??
        review?.upvotes,
      0
    )
  );

function normalizeJudgeMeReview(review) {
  const customFormAnswers = normalizeCustomFormAnswers(review?.cf_answers);
  const pictures = getReviewPictures(review);

  const normalizedReview = {
    id: normalizeText(review?.uuid || review?.id),
    rating: clamp(toInteger(review?.rating, 0), 0, 5),
    title: normalizeText(review?.title),
    body: normalizeText(review?.body || stripHtml(review?.body_html)),
    bodyHtml: normalizeText(review?.body_html),
    reviewerName: normalizeText(review?.reviewer_name || review?.author || 'Anonymous') || 'Anonymous',
    reviewerInitial: normalizeText(review?.reviewer_initial) || normalizeText(review?.reviewer_name || review?.author).charAt(0).toUpperCase(),
    verifiedBuyer: Boolean(review?.verified_buyer),
    createdAt: normalizeText(review?.created_at),
    purchasedVariant: normalizeText(review?.product_variant_title),
    customFormAnswers,
    height: '',
    weight: '',
    fit: '',
    pictures,
    helpfulCount: getReviewHelpfulCount(review),
    replyContent: normalizeText(review?.reply_content),
    shopReplyName: normalizeText(review?.shop_reply_name),
    bodyPreview: buildExcerpt(review?.body || review?.body_html),
    raw: review ?? null,
  };

  normalizedReview.height = getCustomAnswer(normalizedReview, 'height');
  normalizedReview.weight = getCustomAnswer(normalizedReview, 'weight');
  normalizedReview.fit = getCustomAnswer(normalizedReview, 'fit');
  normalizedReview.purchasedVariant =
    normalizedReview.purchasedVariant || getCustomAnswer(normalizedReview, 'purchasedSize');

  return normalizedReview;
}

function getJudgeMeReviews(productId) {
  const data = getJudgeMeReviewWidgetData(productId);
  if (!data || !Array.isArray(data.reviews)) return [];
  return data.reviews.map((review) => normalizeJudgeMeReview(review));
}

const getJudgeMeDomWidget = (section) => {
  if (!(section instanceof HTMLElement)) return null;
  return section.querySelector('#judgeme_product_reviews .jdgm-rev-widg');
};

const parseDomCustomFormAnswers = (reviewNode) => {
  const customFormRoot = reviewNode.querySelector('.jdgm-rev__custom-form');
  if (!(customFormRoot instanceof HTMLElement)) return [];

  const textBlocks = Array.from(customFormRoot.children)
    .map((child) => normalizeText(child.textContent))
    .filter(Boolean);

  const fallbackLines =
    textBlocks.length > 0
      ? textBlocks
      : normalizeText(customFormRoot.textContent)
          .split(/\n+/)
          .map((line) => normalizeText(line))
          .filter(Boolean);

  return fallbackLines
    .map((line) => {
      const [label, ...rest] = line.split(':');
      if (!rest.length) return null;
      return createCustomAnswer(label, rest.join(':').trim(), { line });
    })
    .filter(Boolean);
};

const getJudgeMeDomSummary = (section) => {
  const widget = getJudgeMeDomWidget(section);
  if (!(widget instanceof HTMLElement)) return null;

  const domReviews = getJudgeMeDomReviews(section);
  const reviewRatings = domReviews.map((review) => review.rating).filter((rating) => rating > 0);
  const derivedReviewCount = domReviews.length;
  const derivedAverageRating =
    reviewRatings.length > 0 ? reviewRatings.reduce((sum, rating) => sum + rating, 0) / reviewRatings.length : 0;

  const histogram = Array.from(widget.querySelectorAll('.jdgm-histogram__row[data-rating]')).map((row) => ({
    rating: clamp(toInteger(row.getAttribute('data-rating'), 0), 0, 5),
    frequency: Math.max(0, toInteger(row.getAttribute('data-frequency'), 0)),
    percentage: clamp(toFiniteNumber(row.getAttribute('data-percentage'), 0), 0, 100),
  }));
  const normalizedReviewCount = Math.max(Math.max(0, toInteger(widget.getAttribute('data-number-of-reviews'), 0)), derivedReviewCount);
  const normalizedAverageRating = Math.max(toFiniteNumber(widget.getAttribute('data-average-rating'), 0), derivedAverageRating);
  const normalizedHistogram =
    histogram.some((item) => item.frequency > 0 || item.percentage > 0) || reviewRatings.length === 0
      ? histogram
      : buildHistogramFromRatings(reviewRatings);

  const summary = {
    averageRating: normalizedAverageRating,
    numberOfReviews: normalizedReviewCount,
    numberOfQuestions: Math.max(0, toInteger(widget.getAttribute('data-number-of-questions'), 0)),
    reviewSummaryText: normalizeText(widget.querySelector('.jdgm-rev-widg__summary-text')?.textContent),
    histogram: normalizedHistogram,
  };

  return hasRenderableJudgeMeData({
    average_rating: summary.averageRating,
    number_of_reviews: summary.numberOfReviews,
    histogram: summary.histogram,
  })
    ? summary
    : null;
};

const getJudgeMeDomReviews = (section) => {
  const widget = getJudgeMeDomWidget(section);
  if (!(widget instanceof HTMLElement)) return [];

  return Array.from(widget.querySelectorAll('.jdgm-rev')).map((reviewNode) =>
    normalizeJudgeMeReview({
      uuid: reviewNode.getAttribute('data-review-id') || '',
      rating: reviewNode.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || 0,
      title: reviewNode.querySelector('.jdgm-rev__title')?.textContent || '',
      body_html: reviewNode.querySelector('.jdgm-rev__body')?.innerHTML || '',
      body: reviewNode.querySelector('.jdgm-rev__body')?.textContent || '',
      verified_buyer: reviewNode.getAttribute('data-verified-buyer') === 'true',
      created_at: reviewNode.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '',
      reviewer_name: reviewNode.querySelector('.jdgm-rev__author')?.textContent || '',
      reply_content: reviewNode.querySelector('.jdgm-rev__reply')?.textContent || '',
      pictures_urls: Array.from(reviewNode.querySelectorAll('.jdgm-rev__pics img'))
        .map((image) => image.getAttribute('src') || image.getAttribute('data-src') || '')
        .filter(Boolean),
      pictures: Array.from(
        reviewNode.querySelectorAll(
          '.jdgm-rev__pic img, .jdgm-rev__media img, .jdgm-rev__image img, [class*="pic"] img, [class*="media"] img'
        )
      )
        .map((image) => image.getAttribute('src') || image.getAttribute('data-src') || image.getAttribute('data-original-src') || '')
        .filter(Boolean),
      cf_answers: parseDomCustomFormAnswers(reviewNode),
      helpful_count:
        normalizeText(
          reviewNode.querySelector('.jdgm-rev__votes-up, .jdgm-rev__helpful-count, [class*="helpful" i]')?.textContent || ''
        ).match(/\d+/)?.[0] || 0,
      product_variant_title:
        reviewNode.querySelector('.jdgm-rev__prod-size, .jdgm-rev__variant, [data-variant-title]')?.textContent || '',
    })
  );
};

const createMeasureRange = (start, end = start) => {
  const normalizedStart = Number.parseFloat(start);
  const normalizedEnd = Number.parseFloat(end);
  if (!Number.isFinite(normalizedStart) || !Number.isFinite(normalizedEnd)) return null;

  return {
    min: Math.round(Math.min(normalizedStart, normalizedEnd)),
    max: Math.round(Math.max(normalizedStart, normalizedEnd)),
  };
};

const extractMeasureRange = (value, type) => {
  const normalizedValue = normalizeText(value).toLowerCase();
  if (!normalizedValue) return null;

  if (type === 'height') {
    const feetInchesMatches = Array.from(normalizedValue.matchAll(/(\d+)\s*(?:ft|')\s*(\d+)?/g))
      .map((match) => {
        const feet = Number.parseFloat(match[1]);
        const inches = Number.parseFloat(match[2] || '0');
        return Math.round((feet * 12 + inches) * 2.54);
      })
      .filter((number) => Number.isFinite(number));

    if (feetInchesMatches.length >= 2) {
      return createMeasureRange(feetInchesMatches[0], feetInchesMatches[1]);
    }

    if (feetInchesMatches.length === 1) {
      return createMeasureRange(feetInchesMatches[0]);
    }

    const cmRangeMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*(?:-|~|–|to)\s*(\d+(?:\.\d+)?)\s*(?:cm)?/i);
    if (cmRangeMatch) {
      return createMeasureRange(cmRangeMatch[1], cmRangeMatch[2]);
    }

    const cmMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*cm/i);
    if (cmMatch) {
      return createMeasureRange(cmMatch[1]);
    }
  }

  if (type === 'weight') {
    const poundRangeMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(?:lb|lbs|pounds?)\s*(?:-|~|–|to)\s*(\d+(?:\.\d+)?)\s*(?:lb|lbs|pounds?)/i);
    if (poundRangeMatch) {
      return createMeasureRange(
        Number.parseFloat(poundRangeMatch[1]) * 0.453592,
        Number.parseFloat(poundRangeMatch[2]) * 0.453592
      );
    }

    const kgRangeMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(?:kg)?\s*(?:-|~|–|to)\s*(\d+(?:\.\d+)?)\s*(?:kg)?/i);
    if (kgRangeMatch) {
      return createMeasureRange(kgRangeMatch[1], kgRangeMatch[2]);
    }

    const kgMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*kg/i);
    if (kgMatch) {
      return createMeasureRange(kgMatch[1]);
    }

    const poundsMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(?:lb|lbs|pounds?)/i);
    if (poundsMatch) {
      return createMeasureRange(Number.parseFloat(poundsMatch[1]) * 0.453592);
    }
  }

  const matches = Array.from(normalizedValue.matchAll(/(\d+(?:\.\d+)?)/g))
    .map((match) => Number.parseFloat(match[1]))
    .filter((number) => Number.isFinite(number));

  if (!matches.length) return null;

  if (type === 'height') {
    const heightLike = matches.filter((number) => number >= 100);
    if (heightLike.length >= 2) {
      return createMeasureRange(heightLike[0], heightLike[1]);
    }

    return createMeasureRange(heightLike[0] || matches[0]);
  }

  const weightLike = matches.filter((number) => number >= 25 && number <= 300);
  if (weightLike.length >= 2) {
    return createMeasureRange(weightLike[0], weightLike[1]);
  }

  return createMeasureRange(weightLike[0] || matches[0]);
};

const createBodyFilterRange = (bounds) => ({
  heightMin: bounds.heightMin,
  heightMax: bounds.heightMax,
  weightMin: bounds.weightMin,
  weightMax: bounds.weightMax,
});

const normalizeBodyFilterRange = (range, bounds) => {
  const nextRange = {
    ...createBodyFilterRange(bounds),
    ...(range || {}),
  };

  nextRange.heightMin = clamp(toInteger(nextRange.heightMin, bounds.heightMin), bounds.heightMin, bounds.heightMax);
  nextRange.heightMax = clamp(toInteger(nextRange.heightMax, bounds.heightMax), bounds.heightMin, bounds.heightMax);
  if (nextRange.heightMin > nextRange.heightMax) {
    nextRange.heightMin = nextRange.heightMax;
  }

  nextRange.weightMin = clamp(toInteger(nextRange.weightMin, bounds.weightMin), bounds.weightMin, bounds.weightMax);
  nextRange.weightMax = clamp(toInteger(nextRange.weightMax, bounds.weightMax), bounds.weightMin, bounds.weightMax);
  if (nextRange.weightMin > nextRange.weightMax) {
    nextRange.weightMin = nextRange.weightMax;
  }

  return nextRange;
};

const getBodyFilterBounds = (reviews) => {
  const heightRanges = reviews.map((review) => review.heightRange).filter(Boolean);
  const weightRanges = reviews.map((review) => review.weightRange).filter(Boolean);
  const heightMinValues = heightRanges.map((range) => range.min);
  const heightMaxValues = heightRanges.map((range) => range.max);
  const weightMinValues = weightRanges.map((range) => range.min);
  const weightMaxValues = weightRanges.map((range) => range.max);

  return {
    hasHeightData: heightRanges.length > 0,
    hasWeightData: weightRanges.length > 0,
    heightMin:
      heightMinValues.length > 0
        ? Math.min(DEFAULT_BODY_FILTER_BOUNDS.heightMin, roundDown(Math.min(...heightMinValues), 5))
        : DEFAULT_BODY_FILTER_BOUNDS.heightMin,
    heightMax:
      heightMaxValues.length > 0
        ? Math.max(DEFAULT_BODY_FILTER_BOUNDS.heightMax, roundUp(Math.max(...heightMaxValues), 5))
        : DEFAULT_BODY_FILTER_BOUNDS.heightMax,
    weightMin:
      weightMinValues.length > 0
        ? Math.min(DEFAULT_BODY_FILTER_BOUNDS.weightMin, roundDown(Math.min(...weightMinValues), 5))
        : DEFAULT_BODY_FILTER_BOUNDS.weightMin,
    weightMax:
      weightMaxValues.length > 0
        ? Math.max(DEFAULT_BODY_FILTER_BOUNDS.weightMax, roundUp(Math.max(...weightMaxValues), 5))
        : DEFAULT_BODY_FILTER_BOUNDS.weightMax,
  };
};

const isBodyFilterRangeActive = (range, bounds) =>
  Boolean(
    (bounds.hasHeightData && (range.heightMin > bounds.heightMin || range.heightMax < bounds.heightMax)) ||
      (bounds.hasWeightData && (range.weightMin > bounds.weightMin || range.weightMax < bounds.weightMax))
  );

const rangesOverlap = (sourceRange, filterMin, filterMax) =>
  Boolean(sourceRange) && sourceRange.max >= filterMin && sourceRange.min <= filterMax;

const formatWeightRangeLabel = (minValue, maxValue) => {
  const minPounds = Math.round(minValue / 0.453592);
  const maxPounds = Math.round(maxValue / 0.453592);
  return `${formatMeasure(minValue, 'kg')} (${formatCount(minPounds)} lb) - ${formatMeasure(maxValue, 'kg')} (${formatCount(maxPounds)} lb)`;
};

const formatFeetInches = (valueInCm) => {
  const totalInches = Math.round(valueInCm / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${formatCount(feet)}'${formatCount(inches)}"`;
};

const formatHeightRangeLabel = (minValue, maxValue) => {
  return `${formatMeasure(minValue, 'cm')} (${formatFeetInches(minValue)}) - ${formatMeasure(maxValue, 'cm')} (${formatFeetInches(maxValue)})`;
};

const getRangePercent = (value, min, max) => {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * 100;
};

const sectionState = new WeakMap();
const overlayRoots = new Set();
let overlayEventsBound = false;

const createElement = (tagName, className, textContent) => {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (textContent != null) element.textContent = textContent;
  return element;
};

const formatReviewDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const maskReviewerName = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return '';

  const compact = Array.from(normalized.replace(/\s+/g, ''));
  if (compact.length <= 1) return compact[0] || '';

  const visibleCount = compact.length >= 5 ? 3 : 2;
  const maskedCount = Math.max(2, compact.length - visibleCount);
  return `${compact.slice(0, visibleCount).join('')}${'*'.repeat(maskedCount)}`;
};

const getBodyProfile = (review) => [review.height, review.weight].map((value) => normalizeText(value)).filter(Boolean).join(', ');

const getReviewThumbnail = (review) => normalizeText(review.pictures?.[0]);

const parseOptionNames = (value) => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map((item) => normalizeText(item)).filter(Boolean) : [];
  } catch (error) {
    return [];
  }
};

const formatPurchasedVariant = (variantTitle, optionNames) => {
  const normalizedVariantTitle = normalizeText(variantTitle);
  if (!normalizedVariantTitle || normalizeLookupKey(normalizedVariantTitle) === 'defaulttitle') {
    return '';
  }

  const normalizedOptionNames = Array.isArray(optionNames) ? optionNames.map((item) => normalizeText(item)).filter(Boolean) : [];
  if (!normalizedOptionNames.length) return normalizedVariantTitle;

  const variantParts = normalizedVariantTitle.split(/\s*\/\s*/).map((item) => normalizeText(item)).filter(Boolean);
  if (!variantParts.length) return normalizedVariantTitle;

  if (variantParts.length === normalizedOptionNames.length) {
    return normalizedOptionNames.map((label, index) => `[${label}]${variantParts[index]}`).join(' ');
  }

  return normalizedVariantTitle;
};

const getUniqueBodyProfiles = (reviews) => {
  const profiles = reviews.map((review) => review.bodyProfile).filter(Boolean);
  return Array.from(new Set(profiles));
};

const getUniquePurchasedVariants = (reviews) => {
  const variants = reviews.map((review) => review.purchasedVariantDisplay || review.purchasedVariant).filter(Boolean);
  return Array.from(new Set(variants));
};

const sortReviews = (reviews, sortKey) => {
  const normalizedSortKey = normalizeText(sortKey) || 'recommended';
  const nextReviews = [...reviews];
  const getOriginalIndex = (review) => toInteger(review?.originalIndex, Number.MAX_SAFE_INTEGER);

  const sortByOriginalIndex = (left, right) => getOriginalIndex(left) - getOriginalIndex(right);

  if (normalizedSortKey === 'latest') {
    return nextReviews.sort((left, right) => {
      const leftTime = new Date(left.createdAt).getTime();
      const rightTime = new Date(right.createdAt).getTime();
      const normalizedLeftTime = Number.isFinite(leftTime) ? leftTime : -Infinity;
      const normalizedRightTime = Number.isFinite(rightTime) ? rightTime : -Infinity;
      return normalizedRightTime - normalizedLeftTime || sortByOriginalIndex(left, right);
    });
  }

  if (normalizedSortKey === 'highest') {
    return nextReviews.sort((left, right) => {
      const leftRating = toFiniteNumber(left.rating, 0);
      const rightRating = toFiniteNumber(right.rating, 0);
      return rightRating - leftRating || sortByOriginalIndex(left, right);
    });
  }

  if (normalizedSortKey === 'lowest') {
    return nextReviews.sort((left, right) => {
      const leftRating = toFiniteNumber(left.rating, 0);
      const rightRating = toFiniteNumber(right.rating, 0);
      const leftMissing = leftRating <= 0;
      const rightMissing = rightRating <= 0;
      if (leftMissing && !rightMissing) return 1;
      if (!leftMissing && rightMissing) return -1;
      return leftRating - rightRating || sortByOriginalIndex(left, right);
    });
  }

  return nextReviews.sort(sortByOriginalIndex);
};

const getVariantThumbnail = (reviews, selectedVariant) => {
  const matchedReview = reviews.find((review) => (review.purchasedVariantDisplay || review.purchasedVariant) === selectedVariant && review.thumbnailUrl);
  const fallbackReview = reviews.find((review) => review.thumbnailUrl);
  return normalizeText(matchedReview?.thumbnailUrl || fallbackReview?.thumbnailUrl);
};

const getGalleryItems = (reviews) => {
  const seen = new Set();

  return reviews
    .flatMap((review, reviewIndex) =>
      (Array.isArray(review.pictures) ? review.pictures : []).map((pictureUrl, pictureIndex) => ({
        reviewId: review.id,
        pictureUrl,
        pictureIndex,
        reviewIndex,
        review,
      }))
    )
    .filter((item) => {
      if (!item.pictureUrl || seen.has(item.pictureUrl)) return false;
      seen.add(item.pictureUrl);
      return true;
    })
    .map((item, galleryIndex) => ({
      ...item,
      galleryIndex,
      id: `${item.reviewId || item.reviewIndex}-${item.pictureIndex}`,
    }));
};

const getReviewKey = (review) => normalizeText(review?.id) || `review-${toInteger(review?.originalIndex, 0)}`;

const shouldIgnoreReviewRowToggle = (target, row) => {
  if (!(target instanceof HTMLElement) || !(row instanceof HTMLElement)) return true;

  const interactive = target.closest(REVIEW_ROW_TOGGLE_IGNORE_SELECTOR);
  return Boolean(interactive && interactive !== row);
};

const filterReviews = (reviews, filters) => {
  const hasAnyBodyProfile = reviews.some((review) => review.bodyProfile);
  const bounds = filters.bodyFilterBounds || {
    ...DEFAULT_BODY_FILTER_BOUNDS,
    hasHeightData: false,
    hasWeightData: false,
  };
  const bodyFilterRange = normalizeBodyFilterRange(filters.bodyFilterRange || createBodyFilterRange(bounds), bounds);
  const hasHeightRangeFilter = bounds.hasHeightData && (bodyFilterRange.heightMin > bounds.heightMin || bodyFilterRange.heightMax < bounds.heightMax);
  const hasWeightRangeFilter = bounds.hasWeightData && (bodyFilterRange.weightMin > bounds.weightMin || bodyFilterRange.weightMax < bounds.weightMax);

  return reviews.filter((review) => {
    const reviewVariant = review.purchasedVariantDisplay || review.purchasedVariant;
    const hasBodyProfile = Boolean(review.bodyProfile);

    if (filters.selectedVariant && reviewVariant !== filters.selectedVariant) {
      return false;
    }

    if (filters.selectedBodyProfile && review.bodyProfile !== filters.selectedBodyProfile) {
      return false;
    }

    if (hasHeightRangeFilter) {
      if (!rangesOverlap(review.heightRange, bodyFilterRange.heightMin, bodyFilterRange.heightMax)) {
        return false;
      }
    }

    if (hasWeightRangeFilter) {
      if (!rangesOverlap(review.weightRange, bodyFilterRange.weightMin, bodyFilterRange.weightMax)) {
        return false;
      }
    }

    if (!filters.onlySimilar || !hasAnyBodyProfile) return true;
    return hasBodyProfile;
  });
};

const createStarRow = (rating, className = '') => {
  const stars = createElement('div', `product-reviews__stars ${className}`.trim());
  stars.setAttribute('role', 'img');
  stars.setAttribute('aria-label', `${rating} out of 5 stars`);

  for (let index = 1; index <= 5; index += 1) {
    const star = createElement('span', `product-reviews__star${index <= rating ? ' is-active' : ''}`);
    star.setAttribute('aria-hidden', 'true');
    star.innerHTML = `
      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M10 2.1L12.47 7.12L18.01 7.93L14 11.84L14.95 17.37L10 14.77L5.05 17.37L6 11.84L1.99 7.93L7.53 7.12L10 2.1Z" fill="currentColor" stroke="currentColor" stroke-width="0.72" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    stars.append(star);
  }

  return stars;
};

const createMetaRow = (label, value) => {
  const normalizedValue = normalizeText(value);
  if (!label || !normalizedValue) return null;

  const row = createElement('p', 'product-reviews__item-meta-row');
  row.append(createElement('span', 'product-reviews__item-meta-label', `${label} : `));
  row.append(createElement('span', 'product-reviews__item-meta-value', normalizedValue));
  return row;
};

const buildInlineReviewExpansion = (review, labels, isOpen) => {
  const pictures = Array.isArray(review.pictures) ? review.pictures.filter(Boolean) : [];
  if (!pictures.length) return null;

  const expanded = createElement('div', 'product-reviews__item-expanded');
  expanded.setAttribute('aria-hidden', String(!isOpen));
  expanded.inert = !isOpen;

  const inner = createElement('div', 'product-reviews__item-expanded-inner');
  const media = createElement('div', 'product-reviews__item-expanded-media');

  pictures.forEach((pictureUrl) => {
    const image = document.createElement('img');
    image.className = 'product-reviews__item-expanded-image';
    image.loading = 'lazy';
    image.src = pictureUrl;
    image.alt = 'Review photo';
    media.append(image);
  });

  inner.append(media);

  const bodyCopy = review.body || review.title || review.bodyPreview;
  if (bodyCopy) {
    inner.append(createElement('p', 'product-reviews__item-expanded-body', bodyCopy));
  }

  const actions = createElement('div', 'product-reviews__item-expanded-actions');
  const helpful = createElement('button', 'product-reviews__item-expanded-helpful');
  helpful.type = 'button';
  helpful.dataset.judgeMeInlineHelpful = 'true';
  helpful.setAttribute('aria-pressed', 'false');
  helpful.textContent =
    review.helpfulCount > 0 ? `${labels.photoHelpful} (${formatCount(review.helpfulCount)})` : labels.photoHelpful;
  actions.append(helpful);
  inner.append(actions);

  expanded.append(inner);
  return expanded;
};

const buildReviewItem = (review, labels, expandedReviewId = '') => {
  const item = createElement('article', 'product-reviews__item');
  const reviewKey = getReviewKey(review);
  const isPhotoReview = Array.isArray(review.pictures) && review.pictures.some(Boolean);
  const isOpen = isPhotoReview && expandedReviewId === reviewKey;

  item.dataset.judgeMeReviewRow = 'true';
  item.dataset.judgeMeReviewId = reviewKey;
  if (isPhotoReview) {
    item.dataset.judgeMePhotoReviewRow = 'true';
    item.classList.toggle('is-review-open', isOpen);
    item.tabIndex = 0;
    item.setAttribute('role', 'button');
    item.setAttribute('aria-expanded', String(isOpen));
  }

  const content = createElement('div', 'product-reviews__item-main');
  const header = createElement('div', 'product-reviews__item-header');
  const identity = createElement('div', 'product-reviews__item-identity');
  const ratingRow = createElement('div', 'product-reviews__item-rating-row');

  ratingRow.append(createStarRow(review.rating, 'product-reviews__stars--item'));
  ratingRow.append(createElement('p', 'product-reviews__item-author', maskReviewerName(review.reviewerName)));
  identity.append(ratingRow);

  if (review.verifiedBuyer) {
    identity.append(createElement('span', 'product-reviews__item-badge', labels.verifiedBuyer));
  }

  header.append(identity);
  header.append(createElement('p', 'product-reviews__item-date', formatReviewDate(review.createdAt)));
  item.append(header);

  const meta = createElement('div', 'product-reviews__item-meta');
  [
    createMetaRow(labels.option, review.purchasedVariantDisplay || review.purchasedVariant),
    createMetaRow(labels.height, review.height),
    createMetaRow(labels.weight, review.weight),
    createMetaRow(labels.fitDisplay, review.fit),
  ]
    .filter(Boolean)
    .forEach((row) => meta.append(row));

  if (meta.children.length > 0) {
    content.append(meta);
  }

  const bodyCopy = review.body || review.title || review.bodyPreview;
  if (bodyCopy) {
    content.append(createElement('p', 'product-reviews__item-body', bodyCopy));
  }

  if (review.replyContent) {
    const reply = createElement('div', 'product-reviews__item-reply');
    const replyLabel = review.shopReplyName
      ? `${review.shopReplyName} · ${labels.reply}`
      : labels.reply;
    reply.append(createElement('p', 'product-reviews__item-reply-label', replyLabel));
    reply.append(createElement('p', 'product-reviews__item-reply-body', review.replyContent));
    content.append(reply);
  }

  item.append(content);

  const thumbnailUrl = review.thumbnailUrl;
  if (thumbnailUrl) {
    item.classList.add('has-thumbnail');
    const aside = createElement('div', 'product-reviews__item-aside');
    const image = document.createElement('img');
    image.className = 'product-reviews__item-thumb';
    image.loading = 'lazy';
    image.src = thumbnailUrl;
    image.alt = 'Review image';
    aside.append(image);
    item.append(aside);
  }

  const expanded = buildInlineReviewExpansion(review, labels, isOpen);
  if (expanded) item.append(expanded);

  return item;
};

const buildOverlayReviewMedia = (review) => {
  const pictures = Array.isArray(review.pictures) ? review.pictures.filter(Boolean) : [];
  if (!pictures.length) return null;

  const media = createElement('div', 'product-reviews__overlay-item-media');

  pictures.forEach((pictureUrl) => {
    const item = createElement('div', 'product-reviews__overlay-item-media-item');
    const image = document.createElement('img');
    image.className = 'product-reviews__overlay-item-media-image';
    image.loading = 'lazy';
    image.src = pictureUrl;
    image.alt = 'Review image';
    item.append(image);
    media.append(item);
  });

  return media;
};

const buildOverlayReviewItem = (review, labels) => {
  const item = createElement('article', 'product-reviews__overlay-item');

  const header = createElement('div', 'product-reviews__overlay-item-header');
  const identity = createElement('div', 'product-reviews__overlay-item-identity');
  const ratingRow = createElement('div', 'product-reviews__overlay-item-rating-row');

  ratingRow.append(createStarRow(review.rating, 'product-reviews__stars--item'));
  ratingRow.append(createElement('p', 'product-reviews__overlay-item-author', maskReviewerName(review.reviewerName)));
  identity.append(ratingRow);

  if (review.verifiedBuyer) {
    identity.append(createElement('span', 'product-reviews__overlay-item-badge', labels.verifiedBuyer));
  }

  header.append(identity);
  header.append(createElement('p', 'product-reviews__overlay-item-date', formatReviewDate(review.createdAt)));
  item.append(header);

  const meta = createElement('div', 'product-reviews__overlay-item-meta');
  [
    createMetaRow(labels.option, review.purchasedVariantDisplay || review.purchasedVariant),
    createMetaRow(labels.height, review.height),
    createMetaRow(labels.weight, review.weight),
    createMetaRow(labels.fitDisplay, review.fit),
  ]
    .filter(Boolean)
    .forEach((row) => {
      row.classList.remove('product-reviews__item-meta-row');
      row.classList.add('product-reviews__overlay-item-meta-row');
      meta.append(row);
    });

  if (meta.children.length > 0) {
    item.append(meta);
  }

  const bodyCopy = review.body || review.title || review.bodyPreview;
  if (bodyCopy) {
    item.append(createElement('p', 'product-reviews__overlay-item-body', bodyCopy));
  }

  const media = buildOverlayReviewMedia(review);
  if (media) {
    item.append(media);
  }

  if (review.replyContent) {
    const reply = createElement('div', 'product-reviews__overlay-item-reply');
    const replyLabel = review.shopReplyName
      ? `${review.shopReplyName} · ${labels.reply}`
      : labels.reply;
    reply.append(createElement('p', 'product-reviews__overlay-item-reply-label', replyLabel));
    reply.append(createElement('p', 'product-reviews__overlay-item-reply-body', review.replyContent));
    item.append(reply);
  }

  return item;
};

const renderPhotoGallery = (root, reviews, labels) => {
  const wrap = root.querySelector('[data-judge-me-photo-gallery-wrap]');
  const gallery = root.querySelector('[data-judge-me-photo-gallery]');
  if (!(wrap instanceof HTMLElement) || !(gallery instanceof HTMLElement)) return;

  gallery.replaceChildren();

  const items = getGalleryItems(reviews);
  if (!items.length) {
    wrap.hidden = true;
    return;
  }

  items.slice(0, PRODUCT_PAGE_PHOTO_PREVIEW_LIMIT).forEach((item, index) => {
    const tile = createElement('button', 'product-reviews__gallery-item');
    tile.type = 'button';
    tile.dataset.judgeMeOpenMediaOverlay = 'true';
    tile.dataset.judgeMeMediaIndex = String(item.galleryIndex);
    tile.dataset.judgeMeMediaView = 'detail';

    const image = document.createElement('img');
    image.className = 'product-reviews__gallery-image';
    image.loading = 'lazy';
    image.src = item.pictureUrl;
    image.alt = 'Review image';
    tile.append(image);

    if (index === PRODUCT_PAGE_PHOTO_PREVIEW_LIMIT - 1 && items.length > PRODUCT_PAGE_PHOTO_PREVIEW_LIMIT) {
      tile.dataset.judgeMeMediaView = 'grid';
      tile.dataset.photoGridMore = 'true';
      const overlay = createElement('span', 'product-reviews__gallery-overlay');
      overlay.append(createElement('span', 'product-reviews__gallery-overlay-label', labels.moreMedia));
      overlay.append(
        createElement(
          'span',
          'product-reviews__gallery-overlay-count',
          `+${items.length - PRODUCT_PAGE_PHOTO_PREVIEW_LIMIT}`
        )
      );
      tile.append(overlay);
    }

    gallery.append(tile);
  });

  wrap.hidden = false;
};

const renderFilterControls = (root, state, labels) => {
  const controls = root.querySelector('[data-judge-me-controls]');
  const toggle = root.querySelector('[data-judge-me-similar-toggle]');
  const toggleCount = root.querySelector('[data-judge-me-similar-body-count]');
  const filterWrap = root.querySelector('[data-judge-me-body-filter-wrap]');
  const filterCurrent = root.querySelector('[data-judge-me-body-filter-current]');

  if (!(controls instanceof HTMLElement) || !(toggle instanceof HTMLInputElement)) return;

  const bodyProfiles = getUniqueBodyProfiles(state.reviews);
  const hasBodyProfiles = bodyProfiles.length > 0;
  const hasBodyFilterData = Boolean(state.bodyFilterBounds?.hasHeightData || state.bodyFilterBounds?.hasWeightData || hasBodyProfiles);
  const bodyRangeActive = isBodyFilterRangeActive(state.bodyFilterRange, state.bodyFilterBounds);

  controls.hidden = !hasBodyFilterData;
  if (!hasBodyFilterData) return;

  const similarCount = bodyRangeActive
    ? filterReviews(state.reviews, {
        ...state,
        onlySimilar: false,
        selectedBodyProfile: '',
        selectedVariant: '',
        bodyFilterRange: state.bodyFilterRange,
      }).length
    : state.reviews.filter((review) => review.bodyProfile).length;

  toggle.checked = Boolean(state.onlySimilar);
  if (toggleCount instanceof HTMLElement) {
    toggleCount.textContent = `(${formatCount(similarCount)})`;
  }

  if (filterWrap instanceof HTMLElement) {
    filterWrap.hidden = false;
    filterWrap.classList.toggle('is-active', bodyRangeActive);

    if (filterCurrent instanceof HTMLElement) {
      filterCurrent.textContent = labels.bodyFilter;
    }
  }
};

const renderReviewList = (root, reviews, labels, state) => {
  const list = root.querySelector('[data-judge-me-review-list]');
  if (!(list instanceof HTMLElement)) return;

  list.replaceChildren();

  const usableReviews = reviews.filter(
    (review) => review.rating > 0 || review.body || review.title || review.bodyProfile || review.purchasedVariant || review.thumbnailUrl
  );

  if (!usableReviews.length) {
    list.append(createElement('p', 'product-reviews__empty', labels.noReviews));
    return;
  }

  if (
    state?.expandedPhotoReviewId &&
    !usableReviews.some((review) => getReviewKey(review) === state.expandedPhotoReviewId)
  ) {
    state.expandedPhotoReviewId = '';
  }

  usableReviews.forEach((review) => {
    list.append(buildReviewItem(review, labels, state?.expandedPhotoReviewId || ''));
  });
};

const renderInlineExpandedReviewList = (root, reviews, labels, isVisible) => {
  const list = root.querySelector('[data-judge-me-inline-expanded]');
  if (!(list instanceof HTMLElement)) return;

  list.replaceChildren();
  list.hidden = !isVisible;

  if (!isVisible || !reviews.length) return;

  reviews.forEach((review) => {
    list.append(buildOverlayReviewItem(review, labels));
  });
};

const syncInlinePhotoReviewRows = (root) => {
  const state = sectionState.get(root);
  if (!state) return;

  root.querySelectorAll('[data-judge-me-photo-review-row]').forEach((row) => {
    const isOpen = row.dataset.judgeMeReviewId === state.expandedPhotoReviewId;
    row.classList.toggle('is-review-open', isOpen);
    row.setAttribute('aria-expanded', String(isOpen));
    const expanded = row.querySelector('.product-reviews__item-expanded');
    expanded?.setAttribute('aria-hidden', String(!isOpen));
    if (expanded instanceof HTMLElement) expanded.inert = !isOpen;
  });
};

const toggleInlinePhotoReviewRow = (root, row) => {
  const state = sectionState.get(root);
  if (!state || !(row instanceof HTMLElement)) return;

  const reviewId = row.dataset.judgeMeReviewId || '';
  if (!reviewId) return;

  state.expandedPhotoReviewId = state.expandedPhotoReviewId === reviewId ? '' : reviewId;
  syncInlinePhotoReviewRows(root);
};

const renderOverlayReviewList = (root, reviews, labels) => {
  const list = root.querySelector('[data-judge-me-overlay-review-list]');
  if (!(list instanceof HTMLElement)) return;

  list.replaceChildren();

  if (!reviews.length) {
    list.append(createElement('p', 'product-reviews__overlay-empty', labels.noReviews));
    return;
  }

  reviews.forEach((review) => {
    list.append(buildOverlayReviewItem(review, labels));
  });
};

const renderSummaryHeader = (root, summary, labels) => {
  const summaryTitle = root.querySelector('[data-judge-me-summary-title]');
  const summaryCount = root.querySelector('[data-judge-me-summary-count]');
  const averageStars = root.querySelector('[data-judge-me-average-stars]');

  if (summaryTitle instanceof HTMLElement) {
    summaryTitle.textContent = labels.heading;
  }

  if (summaryCount instanceof HTMLElement) {
    summaryCount.textContent = `(${formatCount(summary.numberOfReviews)})`;
  }

  if (averageStars instanceof HTMLElement) {
    averageStars.replaceChildren(createStarRow(Math.round(summary.averageRating), 'product-reviews__stars--summary'));
  }
};

const getVisibleReviews = (state, overrides = {}) => {
  const nextFilters = {
    ...state,
    ...overrides,
  };
  return sortReviews(filterReviews(state.reviews, nextFilters), nextFilters.sortKey);
};

const bindGlobalOverlayEvents = () => {
  if (overlayEventsBound) return;

  window.addEventListener('popstate', () => {
    overlayRoots.forEach((root) => {
      const state = sectionState.get(root);
      if (!state?.overlayOpen) return;
      state.overlayHistoryPushed = false;
      closeReviewOverlay(root, { skipHistory: true });
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    overlayRoots.forEach((root) => {
      const state = sectionState.get(root);
      if (!state?.overlayOpen) return;

      if (event.key === 'Escape') {
        if (state.sortSheetOpen) {
          closeSortSheet(root);
          return;
        }
        if (state.bodySheetOpen) {
          closeBodyFilterSheet(root);
          return;
        }
        closeReviewOverlay(root);
        return;
      }

      if (state.overlayMode === 'media' && !state.sortSheetOpen && !state.bodySheetOpen) {
        event.preventDefault();
        moveMediaOverlaySelection(root, event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
  });

  overlayEventsBound = true;
};

const setOverlayDocumentState = () => {
  const hasOpenOverlay = Array.from(overlayRoots).some((root) => sectionState.get(root)?.overlayOpen);
  const hasOpenBodySheet = Array.from(overlayRoots).some((root) => sectionState.get(root)?.bodySheetOpen);
  const hasOpenSortSheet = Array.from(overlayRoots).some((root) => sectionState.get(root)?.sortSheetOpen);

  document.documentElement.classList.toggle('is-review-overlay-open', hasOpenOverlay);
  document.body.classList.toggle('is-review-overlay-open', hasOpenOverlay);
  document.documentElement.classList.toggle('is-review-body-sheet-open', hasOpenBodySheet);
  document.body.classList.toggle('is-review-body-sheet-open', hasOpenBodySheet);
  document.documentElement.classList.toggle('is-review-sort-sheet-open', hasOpenSortSheet);
  document.body.classList.toggle('is-review-sort-sheet-open', hasOpenSortSheet);
};

const closeReviewOverlay = (root, { skipHistory = false } = {}) => {
  const overlay = root.querySelector('[data-judge-me-overlay]');
  const mediaOverlay = root.querySelector('[data-judge-me-media-overlay]');
  const state = sectionState.get(root);
  if (!(overlay instanceof HTMLElement) || !(mediaOverlay instanceof HTMLElement) || !state) return;

  overlay.hidden = true;
  overlay.setAttribute('aria-hidden', 'true');
  mediaOverlay.hidden = true;
  mediaOverlay.setAttribute('aria-hidden', 'true');
  state.overlayOpen = false;
  state.overlayMode = 'reviews';
  state.mediaOverlayView = 'detail';
  state.bodySheetOpen = false;
  state.sortSheetOpen = false;
  state.bodyFilterDraft = normalizeBodyFilterRange(state.bodyFilterRange, state.bodyFilterBounds);
  setOverlayDocumentState();

  if (state.lastFocusedElement instanceof HTMLElement) {
    state.lastFocusedElement.focus();
  }

  if (!skipHistory && state.overlayHistoryPushed) {
    state.overlayHistoryPushed = false;
    history.back();
  }
};

const openReviewOverlay = (root, trigger = null, mode = 'reviews') => {
  const reviewOverlay = root.querySelector('[data-judge-me-overlay]');
  const mediaOverlay = root.querySelector('[data-judge-me-media-overlay]');
  const state = sectionState.get(root);
  if (!(reviewOverlay instanceof HTMLElement) || !(mediaOverlay instanceof HTMLElement) || !state) return;

  bindGlobalOverlayEvents();

  reviewOverlay.hidden = true;
  reviewOverlay.setAttribute('aria-hidden', 'true');
  mediaOverlay.hidden = true;
  mediaOverlay.setAttribute('aria-hidden', 'true');

  const overlay = mode === 'media' ? mediaOverlay : reviewOverlay;
  const overlayBody =
    mode === 'media'
      ? overlay.querySelector('.product-reviews__media-overlay-body')
      : overlay.querySelector('.product-reviews__overlay-body');
  const closeButton =
    mode === 'media'
      ? overlay.querySelector('[data-judge-me-media-overlay-close]')
      : overlay.querySelector('[data-judge-me-overlay-close]');

  state.lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
  state.bodySheetOpen = false;
  state.sortSheetOpen = false;
  state.overlayMode = mode;
  state.bodyFilterDraft = normalizeBodyFilterRange(state.bodyFilterRange, state.bodyFilterBounds);
  overlay.hidden = false;
  overlay.setAttribute('aria-hidden', 'false');
  state.overlayOpen = true;
  setOverlayDocumentState();

  if (overlayBody instanceof HTMLElement) {
    overlayBody.scrollTop = 0;
  }

  if (closeButton instanceof HTMLButtonElement) {
    closeButton.focus();
  }

  if (!state.overlayHistoryPushed) {
    history.pushState({ ...(history.state || {}), owReviewOverlay: true }, '');
    state.overlayHistoryPushed = true;
  }
};

const openBodyFilterSheet = (root) => {
  const state = sectionState.get(root);
  if (!state) return;
  if (!state.bodyFilterBounds?.hasHeightData && !state.bodyFilterBounds?.hasWeightData) return;

  state.bodySheetOpen = true;
  state.sortSheetOpen = false;
  state.bodyFilterDraft = normalizeBodyFilterRange(state.bodyFilterRange, state.bodyFilterBounds);
  setOverlayDocumentState();
  renderCustomReviewExperience(root);
};

const closeBodyFilterSheet = (root) => {
  const state = sectionState.get(root);
  if (!state) return;

  state.bodySheetOpen = false;
  state.bodyFilterDraft = normalizeBodyFilterRange(state.bodyFilterRange, state.bodyFilterBounds);
  setOverlayDocumentState();
  renderCustomReviewExperience(root);
};

const openSortSheet = (root) => {
  const state = sectionState.get(root);
  if (!state?.overlayOpen) return;

  state.sortSheetOpen = true;
  state.bodySheetOpen = false;
  setOverlayDocumentState();
  renderCustomReviewExperience(root);
};

const closeSortSheet = (root) => {
  const state = sectionState.get(root);
  if (!state) return;

  state.sortSheetOpen = false;
  setOverlayDocumentState();
  renderCustomReviewExperience(root);
};

const setMediaOverlaySelection = (root, index, view = 'detail') => {
  const state = sectionState.get(root);
  if (!state) return;

  const items = getMediaOverlayItems(state);
  if (!items.length) {
    state.mediaOverlayIndex = 0;
    state.mediaOverlayView = 'grid';
    renderCustomReviewExperience(root);
    return;
  }

  state.mediaOverlayIndex = clamp(toInteger(index, 0), 0, items.length - 1);
  state.mediaOverlayView = view === 'grid' ? 'grid' : 'detail';
  renderCustomReviewExperience(root);
};

const moveMediaOverlaySelection = (root, direction) => {
  const state = sectionState.get(root);
  if (!state) return;

  const items = getMediaOverlayItems(state);
  if (items.length <= 1) return;

  const currentIndex = clamp(toInteger(state.mediaOverlayIndex, 0), 0, items.length - 1);
  const nextIndex = (currentIndex + direction + items.length) % items.length;
  setMediaOverlaySelection(root, nextIndex, 'detail');
};

const updateBodyFilterDraftValue = (root, key, nextValue) => {
  const state = sectionState.get(root);
  if (!state) return;

  const bounds = state.bodyFilterBounds;
  const draft = normalizeBodyFilterRange(state.bodyFilterDraft || state.bodyFilterRange, bounds);
  draft[key] = toInteger(nextValue, draft[key]);

  if (key === 'heightMin' && draft.heightMin > draft.heightMax) {
    draft.heightMax = draft.heightMin;
  }

  if (key === 'heightMax' && draft.heightMax < draft.heightMin) {
    draft.heightMin = draft.heightMax;
  }

  if (key === 'weightMin' && draft.weightMin > draft.weightMax) {
    draft.weightMax = draft.weightMin;
  }

  if (key === 'weightMax' && draft.weightMax < draft.weightMin) {
    draft.weightMin = draft.weightMax;
  }

  state.bodyFilterDraft = normalizeBodyFilterRange(draft, bounds);
  renderCustomReviewExperience(root);
};

const resetBodyFilterSheet = (root) => {
  const state = sectionState.get(root);
  if (!state) return;

  state.bodyFilterDraft = createBodyFilterRange(state.bodyFilterBounds);
  renderCustomReviewExperience(root);
};

const applyBodyFilterSheet = (root) => {
  const state = sectionState.get(root);
  if (!state) return;
  if (!state.bodyFilterBounds?.hasHeightData && !state.bodyFilterBounds?.hasWeightData) return;

  state.bodyFilterRange = normalizeBodyFilterRange(state.bodyFilterDraft, state.bodyFilterBounds);
  state.selectedBodyProfile = '';
  state.bodySheetOpen = false;
  setOverlayDocumentState();
  renderCustomReviewExperience(root);
};

const renderOverlayVariantFilter = (root, state, labels) => {
  const wrap = root.querySelector('[data-judge-me-overlay-variant-wrap]');
  const current = root.querySelector('[data-judge-me-overlay-variant-current]');
  const options = root.querySelector('[data-judge-me-overlay-variant-options]');
  const mediaWrap = root.querySelector('[data-judge-me-overlay-variant-media]');
  const mediaImage = root.querySelector('[data-judge-me-overlay-variant-image]');

  if (!(wrap instanceof HTMLElement) || !(current instanceof HTMLElement) || !(options instanceof HTMLElement)) return;

  const variants = getUniquePurchasedVariants(state.reviews);
  wrap.hidden = variants.length === 0;
  options.replaceChildren();

  if (!variants.length) return;

  const currentLabel = state.selectedVariant || labels.filterAll;
  current.textContent = currentLabel;

  const variantThumb = state.selectedVariant ? getVariantThumbnail(state.reviews, state.selectedVariant) : '';
  if (mediaWrap instanceof HTMLElement && mediaImage instanceof HTMLImageElement) {
    if (variantThumb) {
      mediaImage.src = variantThumb;
      mediaImage.alt = currentLabel;
      mediaWrap.hidden = false;
    } else {
      mediaWrap.hidden = true;
    }
  }

  const allOption = createElement(
    'button',
    `product-reviews__overlay-variant-option${!state.selectedVariant ? ' is-active' : ''}`,
    labels.filterAll
  );
  allOption.type = 'button';
  allOption.dataset.judgeMeOverlayVariantOption = '';
  options.append(allOption);

  variants.forEach((variant) => {
    const option = createElement(
      'button',
      `product-reviews__overlay-variant-option${state.selectedVariant === variant ? ' is-active' : ''}`,
      variant
    );
    option.type = 'button';
    option.dataset.judgeMeOverlayVariantOption = variant;
    options.append(option);
  });
};

const renderOverlayBodyFilter = (root, state, labels) => {
  const wrap = root.querySelector('[data-judge-me-overlay-body-filter-wrap]');
  const current = root.querySelector('[data-judge-me-overlay-body-filter-current]');
  if (!(wrap instanceof HTMLElement) || !(current instanceof HTMLElement)) return;

  const hasBodyFilterData = Boolean(state.bodyFilterBounds?.hasHeightData || state.bodyFilterBounds?.hasWeightData);
  wrap.hidden = !hasBodyFilterData;
  wrap.classList.toggle('is-active', isBodyFilterRangeActive(state.bodyFilterRange, state.bodyFilterBounds));

  if (!hasBodyFilterData) return;

  current.textContent = isBodyFilterRangeActive(state.bodyFilterRange, state.bodyFilterBounds)
    ? `${labels.bodySheetTrigger} · ${labels.bodySheetTriggerActive}`
    : labels.bodySheetTrigger;
};

const renderMediaOverlayBodyFilter = (root, state, labels) => {
  const wrap = root.querySelector('[data-judge-me-media-overlay-body-filter-wrap]');
  const current = root.querySelector('[data-judge-me-media-overlay-body-filter-current]');
  if (!(wrap instanceof HTMLElement) || !(current instanceof HTMLElement)) return;

  const hasBodyFilterData = Boolean(state.bodyFilterBounds?.hasHeightData || state.bodyFilterBounds?.hasWeightData);
  wrap.hidden = !hasBodyFilterData;
  wrap.classList.toggle('is-active', isBodyFilterRangeActive(state.bodyFilterRange, state.bodyFilterBounds));

  if (!hasBodyFilterData) return;

  current.textContent = isBodyFilterRangeActive(state.bodyFilterRange, state.bodyFilterBounds)
    ? `${labels.bodySheetTrigger} · ${labels.bodySheetTriggerActive}`
    : labels.bodySheetTrigger;
};

const renderOverlaySort = (root, state, labels) => {
  const current = root.querySelector('[data-judge-me-overlay-sort-current]');
  const trigger = root.querySelector('[data-judge-me-sort-sheet-open]');
  const sheet = root.querySelector('[data-judge-me-sort-sheet]');
  const options = root.querySelector('[data-judge-me-sort-sheet-options]');
  if (!(current instanceof HTMLElement) || !(options instanceof HTMLElement) || !(sheet instanceof HTMLElement)) return;

  const sortOptions = [
    { key: 'recommended', label: labels.sortRecommended, description: labels.sortRecommendedDescription },
    { key: 'latest', label: labels.sortLatest },
    { key: 'highest', label: labels.sortHighest },
    { key: 'lowest', label: labels.sortLowest },
  ];

  current.textContent = sortOptions.find((item) => item.key === state.sortKey)?.label || labels.sortRecommended;
  sheet.hidden = !state.sortSheetOpen;
  sheet.setAttribute('aria-hidden', String(!state.sortSheetOpen));
  if (trigger instanceof HTMLButtonElement) {
    trigger.setAttribute('aria-expanded', String(Boolean(state.sortSheetOpen)));
  }

  options.replaceChildren();

  sortOptions.forEach((sortOption) => {
    const isActive = state.sortKey === sortOption.key;
    const option = createElement('button', `product-reviews__sort-sheet-option${isActive ? ' is-active' : ''}`);
    option.type = 'button';
    option.dataset.judgeMeSortSheetOption = sortOption.key;
    option.setAttribute('aria-selected', String(isActive));

    const copy = createElement('span', 'product-reviews__sort-sheet-option-copy');
    copy.append(createElement('span', 'product-reviews__sort-sheet-option-title', sortOption.label));
    if (sortOption.description) {
      copy.append(createElement('span', 'product-reviews__sort-sheet-option-description', sortOption.description));
    }

    const check = createElement('span', 'product-reviews__sort-sheet-check');
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML =
      '<svg viewBox="0 0 18 14" fill="none" focusable="false"><path d="M1.75 7.25L6.6 12L16.25 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    option.append(copy, check);
    options.append(option);
  });
};

const renderOverlaySummary = (root, summary, reviews, labels) => {
  const title = root.querySelector('[data-judge-me-overlay-summary-title]');
  const count = root.querySelector('[data-judge-me-overlay-summary-count]');
  const stars = root.querySelector('[data-judge-me-overlay-summary-stars]');

  if (title instanceof HTMLElement) {
    title.textContent = labels.heading;
  }

  if (count instanceof HTMLElement) {
    count.textContent = `(${formatCount(reviews.length)})`;
  }

  if (stars instanceof HTMLElement) {
    stars.replaceChildren(createStarRow(Math.round(summary.averageRating), 'product-reviews__stars--summary'));
  }
};

const renderBodyFilterSheetGroup = ({
  group,
  labelElement,
  valueElement,
  rangeElement,
  trackElement,
  minInput,
  maxInput,
  label,
  minBound,
  maxBound,
  minValue,
  maxValue,
  unit,
  isVisible,
}) => {
  if (
    !(group instanceof HTMLElement) ||
    !(labelElement instanceof HTMLElement) ||
    !(valueElement instanceof HTMLElement) ||
    !(rangeElement instanceof HTMLElement) ||
    !(trackElement instanceof HTMLElement) ||
    !(minInput instanceof HTMLInputElement) ||
    !(maxInput instanceof HTMLInputElement)
  ) {
    return;
  }

  group.hidden = !isVisible;
  if (!isVisible) return;

  labelElement.textContent = label;
  valueElement.textContent =
    unit === 'kg' ? formatWeightRangeLabel(minValue, maxValue) : formatHeightRangeLabel(minValue, maxValue);

  [minInput, maxInput].forEach((input) => {
    input.min = String(minBound);
    input.max = String(maxBound);
    input.step = '1';
  });

  minInput.value = String(minValue);
  maxInput.value = String(maxValue);

  const start = getRangePercent(minValue, minBound, maxBound);
  const end = getRangePercent(maxValue, minBound, maxBound);
  trackElement.style.left = `${start}%`;
  trackElement.style.width = `${Math.max(end - start, 0)}%`;
  rangeElement.style.setProperty('--range-start', `${start}%`);
  rangeElement.style.setProperty('--range-end', `${end}%`);
};

const renderBodyFilterSheet = (root, state, labels) => {
  const sheet = root.querySelector('[data-judge-me-body-sheet]');
  const title = root.querySelector('[data-judge-me-body-sheet-title]');
  const heightGroup = root.querySelector('[data-judge-me-body-sheet-height-group]');
  const heightLabel = root.querySelector('[data-judge-me-body-sheet-height-label]');
  const heightValue = root.querySelector('[data-judge-me-body-sheet-height-value]');
  const heightRange = root.querySelector('[data-judge-me-body-sheet-height-range]');
  const heightTrack = root.querySelector('[data-judge-me-body-sheet-height-track]');
  const heightMinInput = root.querySelector('[data-judge-me-body-sheet-height-min]');
  const heightMaxInput = root.querySelector('[data-judge-me-body-sheet-height-max]');
  const weightGroup = root.querySelector('[data-judge-me-body-sheet-weight-group]');
  const weightLabel = root.querySelector('[data-judge-me-body-sheet-weight-label]');
  const weightValue = root.querySelector('[data-judge-me-body-sheet-weight-value]');
  const weightRange = root.querySelector('[data-judge-me-body-sheet-weight-range]');
  const weightTrack = root.querySelector('[data-judge-me-body-sheet-weight-track]');
  const weightMinInput = root.querySelector('[data-judge-me-body-sheet-weight-min]');
  const weightMaxInput = root.querySelector('[data-judge-me-body-sheet-weight-max]');
  const resetButton = root.querySelector('[data-judge-me-body-sheet-reset]');
  const applyButton = root.querySelector('[data-judge-me-body-sheet-apply]');

  if (!(sheet instanceof HTMLElement)) return;

  const hasBodyFilterData = Boolean(state.bodyFilterBounds?.hasHeightData || state.bodyFilterBounds?.hasWeightData);
  sheet.hidden = !state.bodySheetOpen || !hasBodyFilterData;
  sheet.setAttribute('aria-hidden', String(!state.bodySheetOpen || !hasBodyFilterData));

  if (!(title instanceof HTMLElement) || !(resetButton instanceof HTMLButtonElement) || !(applyButton instanceof HTMLButtonElement)) {
    return;
  }

  title.textContent = labels.bodySheetTitle;
  resetButton.textContent = labels.bodySheetReset;

  if (!hasBodyFilterData) return;

  const draftRange = normalizeBodyFilterRange(state.bodyFilterDraft || state.bodyFilterRange, state.bodyFilterBounds);
  state.bodyFilterDraft = draftRange;

  renderBodyFilterSheetGroup({
    group: heightGroup,
    labelElement: heightLabel,
    valueElement: heightValue,
    rangeElement: heightRange,
    trackElement: heightTrack,
    minInput: heightMinInput,
    maxInput: heightMaxInput,
    label: labels.bodySheetHeight,
    minBound: state.bodyFilterBounds.heightMin,
    maxBound: state.bodyFilterBounds.heightMax,
    minValue: draftRange.heightMin,
    maxValue: draftRange.heightMax,
    unit: 'cm',
    isVisible: state.bodyFilterBounds.hasHeightData,
  });

  renderBodyFilterSheetGroup({
    group: weightGroup,
    labelElement: weightLabel,
    valueElement: weightValue,
    rangeElement: weightRange,
    trackElement: weightTrack,
    minInput: weightMinInput,
    maxInput: weightMaxInput,
    label: labels.bodySheetWeight,
    minBound: state.bodyFilterBounds.weightMin,
    maxBound: state.bodyFilterBounds.weightMax,
    minValue: draftRange.weightMin,
    maxValue: draftRange.weightMax,
    unit: 'kg',
    isVisible: state.bodyFilterBounds.hasWeightData,
  });

  const draftVisibleReviews = getVisibleReviews(state, { bodyFilterRange: draftRange });
  applyButton.textContent = `${labels.bodySheetView} ${formatCount(draftVisibleReviews.length)} ${labels.bodySheetReviews}`;
};

const getMediaOverlayReviews = (state) =>
  filterReviews(state.reviews, {
    ...state,
    onlySimilar: false,
    selectedBodyProfile: '',
    selectedVariant: '',
    bodyFilterRange: state.bodyFilterRange,
  });

const getMediaOverlayItems = (state) => getGalleryItems(getMediaOverlayReviews(state));

const getReviewMetaRows = (review, labels) =>
  [
    { label: labels.option, value: review.purchasedVariantDisplay || review.purchasedVariant },
    { label: labels.height, value: review.height },
    { label: labels.weight, value: review.weight },
    { label: labels.fitDisplay, value: review.fit },
  ].filter((item) => normalizeText(item.value));

const createMediaNavButton = (direction, labels) => {
  const isPrevious = direction === 'prev';
  const button = createElement(
    'button',
    `product-reviews__media-detail-nav product-reviews__media-detail-nav--${direction}`
  );
  button.type = 'button';
  button.dataset[isPrevious ? 'judgeMeMediaOverlayPrev' : 'judgeMeMediaOverlayNext'] = 'true';
  button.setAttribute('aria-label', isPrevious ? labels.photoPrevious : labels.photoNext);
  button.innerHTML = `
    <svg viewBox="0 0 16 28" fill="none" aria-hidden="true" focusable="false">
      <path d="${isPrevious ? 'M11.5 3L4.5 14L11.5 25' : 'M4.5 3L11.5 14L4.5 25'}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  return button;
};

const buildMediaOverlayDetail = (item, items, labels) => {
  const review = item.review || {};
  const detail = createElement('article', 'product-reviews__media-detail');
  detail.dataset.reviewId = item.reviewId || '';

  const stage = createElement('div', 'product-reviews__media-detail-stage');
  const image = document.createElement('img');
  image.className = 'product-reviews__media-detail-image';
  image.src = item.pictureUrl;
  image.alt = 'Photo review image';
  image.loading = 'eager';
  stage.append(image);

  if (items.length > 1) {
    stage.append(createMediaNavButton('prev', labels), createMediaNavButton('next', labels));
  }

  const content = createElement('div', 'product-reviews__media-detail-content');
  const header = createElement('div', 'product-reviews__media-detail-header');
  const identity = createElement('div', 'product-reviews__media-detail-identity');
  identity.append(createElement('p', 'product-reviews__media-detail-author', maskReviewerName(review.reviewerName)));
  identity.append(createStarRow(review.rating, 'product-reviews__stars--item product-reviews__stars--media-detail'));
  header.append(identity);

  const date = formatReviewDate(review.createdAt);
  if (date) {
    header.append(createElement('p', 'product-reviews__media-detail-date', date));
  }
  content.append(header);

  const metaRows = getReviewMetaRows(review, labels);
  if (metaRows.length > 0) {
    const meta = createElement('div', 'product-reviews__media-detail-meta');
    metaRows.forEach((row) => {
      const metaRow = createElement('p', 'product-reviews__media-detail-meta-row');
      metaRow.append(createElement('span', 'product-reviews__media-detail-meta-label', `${row.label}: `));
      metaRow.append(createElement('span', 'product-reviews__media-detail-meta-value', row.value));
      meta.append(metaRow);
    });
    content.append(meta);
  }

  const bodyCopy = review.body || review.title || review.bodyPreview;
  if (bodyCopy) {
    content.append(createElement('p', 'product-reviews__media-detail-body', bodyCopy));
  }

  if (review.replyContent) {
    const reply = createElement('div', 'product-reviews__media-detail-reply');
    const replyLabel = review.shopReplyName
      ? `${review.shopReplyName} · ${labels.reply}`
      : labels.reply;
    reply.append(createElement('p', 'product-reviews__media-detail-reply-label', replyLabel));
    reply.append(createElement('p', 'product-reviews__media-detail-reply-body', review.replyContent));
    content.append(reply);
  }

  const actions = createElement('div', 'product-reviews__media-detail-actions');
  const helpful = createElement('button', 'product-reviews__media-detail-helpful');
  helpful.type = 'button';
  helpful.dataset.judgeMeMediaHelpful = 'true';
  helpful.setAttribute('aria-pressed', 'false');
  helpful.textContent =
    review.helpfulCount > 0 ? `${labels.photoHelpful} (${formatCount(review.helpfulCount)})` : labels.photoHelpful;

  const viewAll = createElement('button', 'product-reviews__media-detail-view-all', labels.photoViewAll);
  viewAll.type = 'button';
  viewAll.dataset.judgeMeMediaOverlayViewAll = 'true';

  actions.append(helpful, viewAll);
  content.append(actions);
  detail.append(stage, content);
  return detail;
};

const renderMediaOverlayGrid = (grid, items) => {
  grid.replaceChildren();

  items.forEach((item) => {
    const tile = createElement('button', 'product-reviews__media-overlay-item');
    tile.type = 'button';
    tile.dataset.judgeMeMediaOverlayGridItem = 'true';
    tile.dataset.judgeMeMediaIndex = String(item.galleryIndex);

    const image = document.createElement('img');
    image.className = 'product-reviews__media-overlay-image';
    image.loading = 'lazy';
    image.src = item.pictureUrl;
    image.alt = 'Photo review image';
    tile.append(image);
    grid.append(tile);
  });
};

const renderMediaOverlay = (root, reviews, labels) => {
  const title = root.querySelector('[data-judge-me-media-overlay-title]');
  const viewer = root.querySelector('[data-judge-me-media-overlay-viewer]');
  const grid = root.querySelector('[data-judge-me-media-overlay-grid]');
  const empty = root.querySelector('[data-judge-me-media-overlay-empty]');
  const state = sectionState.get(root);
  if (!(viewer instanceof HTMLElement) || !(grid instanceof HTMLElement) || !(empty instanceof HTMLElement) || !state) return;

  if (title instanceof HTMLElement) {
    title.textContent = labels.photoOverlayTitle;
  }

  viewer.replaceChildren();
  grid.replaceChildren();

  const items = getGalleryItems(reviews);
  if (!items.length) {
    empty.textContent = labels.noPhotoReviews;
    empty.hidden = false;
    viewer.hidden = true;
    grid.hidden = true;
    return;
  }

  empty.hidden = true;
  state.mediaOverlayIndex = clamp(toInteger(state.mediaOverlayIndex, 0), 0, items.length - 1);
  state.mediaOverlayView = state.mediaOverlayView === 'grid' ? 'grid' : 'detail';

  if (state.mediaOverlayView === 'grid') {
    viewer.hidden = true;
    grid.hidden = false;
    renderMediaOverlayGrid(grid, items);
    return;
  }

  grid.hidden = true;
  viewer.hidden = false;
  viewer.append(buildMediaOverlayDetail(items[state.mediaOverlayIndex], items, labels));
};

const renderCustomReviewExperience = (root) => {
  const state = sectionState.get(root);
  if (!state) return;

  const labels = {
    overlayTitle: root.dataset.overlayTitleLabel || root.dataset.reviewHeadingLabel || root.dataset.reviewsLabel || 'Reviews',
    photoOverlayTitle: root.dataset.photoOverlayTitleLabel || 'View all photo reviews',
    photoViewAll: root.dataset.photoOverlayViewAllLabel || 'View all photo reviews',
    photoHelpful: root.dataset.photoOverlayHelpfulLabel || 'Helpful',
    photoPrevious: root.dataset.photoOverlayPreviousLabel || 'Previous photo review',
    photoNext: root.dataset.photoOverlayNextLabel || 'Next photo review',
    heading: root.dataset.reviewHeadingLabel || root.dataset.reviewsLabel || 'Reviews',
    reviews: root.dataset.reviewsLabel || 'Reviews',
    viewAll: root.dataset.viewAllLabel || 'View all reviews',
    noReviews: root.dataset.noReviewsLabel || 'No reviews yet.',
    noPhotoReviews: root.dataset.photoOverlayEmptyLabel || 'No photo reviews yet.',
    verifiedBuyer: root.dataset.verifiedBuyerLabel || 'Verified buyer',
    option: root.dataset.reviewOptionLabel || root.dataset.purchasedLabel || 'Option',
    height: root.dataset.heightLabel || 'Height',
    weight: root.dataset.weightLabel || 'Weight',
    bodyProfile: root.dataset.bodyProfileLabel || 'Body',
    fitDisplay: root.dataset.fitDisplayLabel || root.dataset.fitLabel || 'Fit',
    reply: root.dataset.reviewStoreReplyLabel || 'Store reply',
    similarBody: root.dataset.similarBodyLabel || 'Similar body type',
    bodyFilter: root.dataset.bodyFilterLabel || 'Body type',
    filterAll: root.dataset.filterAllLabel || 'All',
    moreMedia: root.dataset.mediaMoreLabel || 'More',
    sortRecommended: root.dataset.sortRecommendedLabel || 'Recommended',
    sortLatest: root.dataset.sortLatestLabel || 'Latest',
    sortHighest: root.dataset.sortHighestLabel || 'Highest rating',
    sortLowest: root.dataset.sortLowestLabel || 'Lowest rating',
    sortRecommendedDescription:
      root.dataset.sortRecommendedDescription ||
      'Sort by review text, photos, body information, creation date, and helpfulness.',
    viewFull: root.dataset.viewFullLabel || root.dataset.viewAllLabel || 'View all product reviews',
    hideFull: root.dataset.hideFullLabel || 'Hide product reviews',
    bodySheetTrigger: root.dataset.bodySheetTriggerLabel || 'Body Type',
    bodySheetTriggerActive: root.dataset.bodySheetTriggerActiveLabel || 'Filtered',
    bodySheetTitle: root.dataset.bodySheetTitleLabel || 'Body Filter',
    bodySheetHeight: root.dataset.bodySheetHeightLabel || 'Height',
    bodySheetWeight: root.dataset.bodySheetWeightLabel || 'Weight',
    bodySheetReset: root.dataset.bodySheetResetLabel || 'Reset',
    bodySheetView: root.dataset.bodySheetViewLabel || 'View',
    bodySheetReviews: root.dataset.bodySheetReviewsLabel || 'Reviews',
  };

  const previewBodyFilterRange = state.onlySimilar
    ? state.bodyFilterRange
    : createBodyFilterRange(state.bodyFilterBounds);
  const filteredReviews = filterReviews(state.reviews, {
    ...state,
    bodyFilterRange: previewBodyFilterRange,
  });
  const visibleReviews = getVisibleReviews(state);
  const usesInlineExpandedLayout = isInlineExpandedReviewLayout();
  const mediaOverlayReviews = getMediaOverlayReviews(state);
  const previewVisibleReviews = getVisibleReviews(state, {
    bodyFilterRange: previewBodyFilterRange,
  }).slice(0, PRODUCT_PAGE_REVIEW_PREVIEW_LIMIT);
  const inlineExpandedReviews =
    usesInlineExpandedLayout && state.inlineExpanded
      ? visibleReviews.slice(PRODUCT_PAGE_REVIEW_PREVIEW_LIMIT)
      : [];
  renderSummaryHeader(root, state.summary, labels);
  renderPhotoGallery(root, filteredReviews, labels);
  renderFilterControls(root, state, labels);
  renderReviewList(root, previewVisibleReviews, labels, state);
  renderInlineExpandedReviewList(root, inlineExpandedReviews, labels, usesInlineExpandedLayout && state.inlineExpanded);
  renderOverlayVariantFilter(root, state, labels);
  renderOverlayBodyFilter(root, state, labels);
  renderMediaOverlayBodyFilter(root, state, labels);
  renderOverlaySort(root, state, labels);
  renderOverlaySummary(root, state.summary, visibleReviews, labels);
  renderOverlayReviewList(root, visibleReviews, labels);
  renderMediaOverlay(root, mediaOverlayReviews, labels);
  renderBodyFilterSheet(root, state, labels);

  const allReviewsLink = root.querySelector('[data-judge-me-all-reviews-link]');
  if (allReviewsLink instanceof HTMLButtonElement) {
    const allReviewsLinkLabel = allReviewsLink.querySelector('.product-reviews__all-link-label');
    const hasExtraVisibleReviews = visibleReviews.length > PRODUCT_PAGE_REVIEW_PREVIEW_LIMIT;
    if (allReviewsLinkLabel instanceof HTMLElement) {
      allReviewsLinkLabel.textContent =
        usesInlineExpandedLayout && state.inlineExpanded ? labels.hideFull : `${labels.viewFull} (${formatCount(state.summary.numberOfReviews)})`;
    } else {
      allReviewsLink.textContent =
        usesInlineExpandedLayout && state.inlineExpanded ? labels.hideFull : `${labels.viewFull} (${formatCount(state.summary.numberOfReviews)})`;
    }
    allReviewsLink.hidden = !hasExtraVisibleReviews;
    allReviewsLink.setAttribute('aria-expanded', usesInlineExpandedLayout ? String(Boolean(state.inlineExpanded)) : 'false');
  }

  const overlayTitle = root.querySelector('[data-judge-me-overlay-title]');
  if (overlayTitle instanceof HTMLElement) {
    overlayTitle.textContent = labels.overlayTitle;
  }

  root.hidden = false;
};

const syncOfficialWidgetVisibility = (section) => {
  if (!(section instanceof HTMLElement)) return;
  section.classList.add('is-judge-me-enhanced');
};

const observeJudgeMeWidget = (root, section, tryRender) => {
  if (!(root instanceof HTMLElement) || !(section instanceof HTMLElement) || typeof MutationObserver !== 'function') {
    return null;
  }

  const observerTarget = section.querySelector('#judgeme_product_reviews') || section;
  if (!(observerTarget instanceof HTMLElement)) return null;

  const observer = new MutationObserver(() => {
    const hasCompleteWidgetData = tryRender();
    if (hasCompleteWidgetData) {
      root.dataset.judgeMeReady = 'true';
      observer.disconnect();
    }
  });

  observer.observe(observerTarget, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-number-of-questions', 'data-score'],
  });

  return observer;
};

const bindRootInteractions = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.judgeMeInteractionsBound === 'true') return;

  overlayRoots.add(root);

  root.addEventListener('change', (event) => {
    const toggle = event.target;
    if (!(toggle instanceof HTMLElement) || !toggle.matches('[data-judge-me-similar-toggle]')) return;

    const state = sectionState.get(root);
    if (!state) return;

    state.onlySimilar = Boolean(toggle.checked);
    renderCustomReviewExperience(root);
  });

  root.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-judge-me-body-sheet-height-min]')) {
      updateBodyFilterDraftValue(root, 'heightMin', target.value);
      return;
    }

    if (target.matches('[data-judge-me-body-sheet-height-max]')) {
      updateBodyFilterDraftValue(root, 'heightMax', target.value);
      return;
    }

    if (target.matches('[data-judge-me-body-sheet-weight-min]')) {
      updateBodyFilterDraftValue(root, 'weightMin', target.value);
      return;
    }

    if (target.matches('[data-judge-me-body-sheet-weight-max]')) {
      updateBodyFilterDraftValue(root, 'weightMax', target.value);
    }
  });

  root.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;

    const closeBodySheetTrigger = target.closest('[data-judge-me-body-sheet-close]');
    if (closeBodySheetTrigger instanceof HTMLElement) {
      event.preventDefault();
      closeBodyFilterSheet(root);
      return;
    }

    const closeSortSheetTrigger = target.closest('[data-judge-me-sort-sheet-close]');
    if (closeSortSheetTrigger instanceof HTMLElement) {
      event.preventDefault();
      closeSortSheet(root);
      return;
    }

    const closeOverlayTrigger = target.closest('[data-judge-me-overlay-close]');
    if (closeOverlayTrigger instanceof HTMLElement) {
      event.preventDefault();
      closeReviewOverlay(root);
      return;
    }

    const closeMediaOverlayTrigger = target.closest('[data-judge-me-media-overlay-close]');
    if (closeMediaOverlayTrigger instanceof HTMLElement) {
      event.preventDefault();
      closeReviewOverlay(root);
      return;
    }

    if (target.matches('[data-judge-me-overlay]')) {
      event.preventDefault();
      closeReviewOverlay(root);
      return;
    }

    if (target.matches('[data-judge-me-media-overlay]')) {
      event.preventDefault();
      closeReviewOverlay(root);
      return;
    }

    const openOverlayTrigger = target.closest('[data-judge-me-open-overlay]');
    if (openOverlayTrigger instanceof HTMLElement) {
      event.preventDefault();
      const state = sectionState.get(root);
      if (state && isInlineExpandedReviewLayout()) {
        state.inlineExpanded = !state.inlineExpanded;
        renderCustomReviewExperience(root);

        if (state.inlineExpanded) {
          const inlineExpanded = root.querySelector('[data-judge-me-inline-expanded]');
          if (inlineExpanded instanceof HTMLElement) {
            inlineExpanded.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }
        return;
      }

      openReviewOverlay(root, openOverlayTrigger, 'reviews');
      return;
    }

    const openMediaOverlayTrigger = target.closest('[data-judge-me-open-media-overlay]');
    if (openMediaOverlayTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      const mediaIndex = toInteger(openMediaOverlayTrigger.dataset.judgeMeMediaIndex, 0);
      const mediaView = openMediaOverlayTrigger.dataset.judgeMeMediaView === 'grid' ? 'grid' : 'detail';
      setMediaOverlaySelection(root, mediaIndex, mediaView);
      openReviewOverlay(root, openMediaOverlayTrigger, 'media');
      return;
    }

    const mediaGridItem = target.closest('[data-judge-me-media-overlay-grid-item]');
    if (mediaGridItem instanceof HTMLButtonElement) {
      event.preventDefault();
      setMediaOverlaySelection(root, mediaGridItem.dataset.judgeMeMediaIndex, 'detail');
      return;
    }

    const mediaPrevious = target.closest('[data-judge-me-media-overlay-prev]');
    if (mediaPrevious instanceof HTMLButtonElement) {
      event.preventDefault();
      moveMediaOverlaySelection(root, -1);
      return;
    }

    const mediaNext = target.closest('[data-judge-me-media-overlay-next]');
    if (mediaNext instanceof HTMLButtonElement) {
      event.preventDefault();
      moveMediaOverlaySelection(root, 1);
      return;
    }

    const mediaViewAll = target.closest('[data-judge-me-media-overlay-view-all]');
    if (mediaViewAll instanceof HTMLButtonElement) {
      event.preventDefault();
      setMediaOverlaySelection(root, 0, 'grid');
      return;
    }

    const mediaHelpful = target.closest('[data-judge-me-media-helpful]');
    if (mediaHelpful instanceof HTMLButtonElement) {
      event.preventDefault();
      const isPressed = mediaHelpful.getAttribute('aria-pressed') === 'true';
      mediaHelpful.setAttribute('aria-pressed', String(!isPressed));
      mediaHelpful.classList.toggle('is-active', !isPressed);
      return;
    }

    const openOverlayBodySheetTrigger = target.closest('[data-judge-me-open-overlay-body-sheet]');
    if (openOverlayBodySheetTrigger instanceof HTMLElement) {
      event.preventDefault();
      const state = sectionState.get(root);
      if (state && isInlineExpandedReviewLayout()) {
        state.inlineExpanded = true;
        renderCustomReviewExperience(root);
        openBodyFilterSheet(root);
        return;
      }

      openReviewOverlay(root, openOverlayBodySheetTrigger, 'reviews');
      openBodyFilterSheet(root);
      return;
    }

    const openBodySheetTrigger = target.closest('[data-judge-me-body-sheet-open]');
    if (openBodySheetTrigger instanceof HTMLElement) {
      event.preventDefault();
      openBodyFilterSheet(root);
      return;
    }

    const openSortSheetTrigger = target.closest('[data-judge-me-sort-sheet-open]');
    if (openSortSheetTrigger instanceof HTMLElement) {
      event.preventDefault();
      openSortSheet(root);
      return;
    }

    const filterOption = target.closest('[data-judge-me-body-filter-option]');
    if (filterOption instanceof HTMLButtonElement) {
      const state = sectionState.get(root);
      if (!state) return;

      state.selectedBodyProfile = filterOption.dataset.judgeMeBodyFilterOption || '';
      const filterWrap = root.querySelector('[data-judge-me-body-filter-wrap]');
      if (filterWrap instanceof HTMLDetailsElement) {
        filterWrap.open = false;
      }
      renderCustomReviewExperience(root);
      return;
    }

    const resetBodySheetTrigger = target.closest('[data-judge-me-body-sheet-reset]');
    if (resetBodySheetTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      resetBodyFilterSheet(root);
      return;
    }

    const applyBodySheetTrigger = target.closest('[data-judge-me-body-sheet-apply]');
    if (applyBodySheetTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      applyBodyFilterSheet(root);
      return;
    }

    const overlayVariantOption = target.closest('[data-judge-me-overlay-variant-option]');
    if (overlayVariantOption instanceof HTMLButtonElement) {
      const state = sectionState.get(root);
      if (!state) return;

      state.selectedVariant = overlayVariantOption.dataset.judgeMeOverlayVariantOption || '';
      const variantWrap = root.querySelector('[data-judge-me-overlay-variant-wrap]');
      if (variantWrap instanceof HTMLDetailsElement) {
        variantWrap.open = false;
      }
      renderCustomReviewExperience(root);
      return;
    }

    const overlaySortOption = target.closest('[data-judge-me-overlay-sort-option]');
    if (overlaySortOption instanceof HTMLButtonElement) {
      const state = sectionState.get(root);
      if (!state) return;

      state.sortKey = overlaySortOption.dataset.judgeMeOverlaySortOption || 'recommended';
      state.sortSheetOpen = false;
      setOverlayDocumentState();
      renderCustomReviewExperience(root);
      return;
    }

    const sortSheetOption = target.closest('[data-judge-me-sort-sheet-option]');
    if (sortSheetOption instanceof HTMLButtonElement) {
      const state = sectionState.get(root);
      if (!state) return;

      state.sortKey = sortSheetOption.dataset.judgeMeSortSheetOption || 'recommended';
      state.sortSheetOpen = false;
      setOverlayDocumentState();
      renderCustomReviewExperience(root);
      return;
    }

    const inlineHelpful = target.closest('[data-judge-me-inline-helpful]');
    if (inlineHelpful instanceof HTMLButtonElement) {
      event.preventDefault();
      const isPressed = inlineHelpful.getAttribute('aria-pressed') === 'true';
      inlineHelpful.setAttribute('aria-pressed', String(!isPressed));
      inlineHelpful.classList.toggle('is-active', !isPressed);
      return;
    }

    const photoReviewRow = target.closest('[data-judge-me-photo-review-row]');
    if (photoReviewRow instanceof HTMLElement && !shouldIgnoreReviewRowToggle(target, photoReviewRow)) {
      event.preventDefault();
      toggleInlinePhotoReviewRow(root, photoReviewRow);
      return;
    }
  });

  root.addEventListener('keydown', (event) => {
    const state = sectionState.get(root);
    if (event.key === 'Escape' && state?.expandedPhotoReviewId && !state.overlayOpen) {
      event.preventDefault();
      state.expandedPhotoReviewId = '';
      syncInlinePhotoReviewRows(root);
      return;
    }

    if (!['Enter', ' '].includes(event.key)) return;

    const target = event.target instanceof HTMLElement ? event.target : null;
    const photoReviewRow = target?.closest('[data-judge-me-photo-review-row]');
    if (!(photoReviewRow instanceof HTMLElement) || shouldIgnoreReviewRowToggle(target, photoReviewRow)) return;

    event.preventDefault();
    toggleInlinePhotoReviewRow(root, photoReviewRow);
  });

  root.dataset.judgeMeInteractionsBound = 'true';
};

const renderReviewSection = (root, summary, reviews) => {
  const state = sectionState.get(root) || {
    selectedBodyProfile: '',
    onlySimilar: false,
    selectedVariant: '',
    sortKey: 'recommended',
    overlayOpen: false,
    overlayMode: 'reviews',
	    mediaOverlayIndex: 0,
	    mediaOverlayView: 'detail',
	    overlayHistoryPushed: false,
	    inlineExpanded: false,
	    expandedPhotoReviewId: '',
	    bodySheetOpen: false,
	    sortSheetOpen: false,
    bodyFilterRange: null,
    bodyFilterDraft: null,
    bodyFilterBounds: {
      ...DEFAULT_BODY_FILTER_BOUNDS,
      hasHeightData: false,
      hasWeightData: false,
    },
    lastFocusedElement: null,
  };
  const optionNames = parseOptionNames(root.dataset.productOptionNames);

  const normalizedReviews = reviews.map((review, index) => ({
    ...review,
    originalIndex: toInteger(review.originalIndex, index),
    purchasedVariantDisplay: formatPurchasedVariant(review.purchasedVariant, optionNames),
    bodyProfile: getBodyProfile(review),
    thumbnailUrl: getReviewThumbnail(review),
    heightRange: extractMeasureRange(review.height, 'height'),
    weightRange: extractMeasureRange(review.weight, 'weight'),
  }));
  const bodyFilterBounds = getBodyFilterBounds(normalizedReviews);

  if (state.selectedBodyProfile && !normalizedReviews.some((review) => review.bodyProfile === state.selectedBodyProfile)) {
    state.selectedBodyProfile = '';
  }

  if (
    state.selectedVariant &&
    !normalizedReviews.some((review) => (review.purchasedVariantDisplay || review.purchasedVariant) === state.selectedVariant)
  ) {
    state.selectedVariant = '';
  }

  if (!['recommended', 'latest', 'highest', 'lowest'].includes(state.sortKey)) {
    state.sortKey = 'recommended';
  }

  state.bodyFilterBounds = bodyFilterBounds;
  state.bodyFilterRange = normalizeBodyFilterRange(state.bodyFilterRange || createBodyFilterRange(bodyFilterBounds), bodyFilterBounds);
  state.bodyFilterDraft = normalizeBodyFilterRange(state.bodyFilterDraft || state.bodyFilterRange, bodyFilterBounds);
  state.summary = summary;
  state.reviews = normalizedReviews;

  sectionState.set(root, state);
  bindRootInteractions(root);
  renderCustomReviewExperience(root);
};

const hydrateJudgeMeSection = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.judgeMeReady === 'true') return;

  const productId = root.dataset.judgeMeProductId;
  if (!productId) return;

  const section = root.closest('.product-reviews');

  const tryRender = () => {
    const widgetSummary = getJudgeMeReviewSummary(productId);
    const widgetReviews = widgetSummary ? getJudgeMeReviews(productId) : [];
    const hasCompleteWidgetData = Boolean(widgetSummary) && (widgetSummary.numberOfReviews === 0 || widgetReviews.length > 0);
    const summary = widgetSummary || getJudgeMeDomSummary(section);
    if (!summary) return false;

    const normalizedReviews = widgetReviews.length > 0 ? widgetReviews : getJudgeMeDomReviews(section);

    renderReviewSection(root, summary, normalizedReviews);
    syncOfficialWidgetVisibility(section);
    return hasCompleteWidgetData;
  };

  if (tryRender()) {
    root.dataset.judgeMeReady = 'true';
    return;
  }

  const widgetObserver = observeJudgeMeWidget(root, section, tryRender);

  let elapsed = 0;
  const pollTimer = window.setInterval(() => {
    elapsed += POLL_INTERVAL_MS;

    const hasCompleteWidgetData = tryRender();
    if (hasCompleteWidgetData || elapsed >= MAX_POLL_MS) {
      window.clearInterval(pollTimer);
      if (!root.hidden) {
        root.dataset.judgeMeReady = 'true';
      }
      if (hasCompleteWidgetData && widgetObserver) {
        widgetObserver.disconnect();
      }
    }
  }, POLL_INTERVAL_MS);
};

const initJudgeMeCustomReviews = () => {
  document.querySelectorAll('[data-judge-me-custom-root]').forEach((root) => {
    hydrateJudgeMeSection(root);
  });
};

window.OWJudgeMe = {
  getJudgeMeReviewWidgetData,
  getJudgeMeReviewSummary,
  getJudgeMeReviews,
  normalizeJudgeMeReview,
  getCustomAnswer,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initJudgeMeCustomReviews, { once: true });
} else {
  initJudgeMeCustomReviews();
}

window.addEventListener('load', initJudgeMeCustomReviews, { once: true });
document.addEventListener('shopify:section:load', initJudgeMeCustomReviews);
